/**
 * Bilder für Unterschriften/Stempel vorbereiten, bevor sie an den Server gehen.
 * Der Server nimmt nur PNG/JPEG bis 512 KB (`pdf-baustein.regeln.ts`) — hier wird
 * darum verkleinert, ein weißer Scan-Hintergrund durchsichtig gemacht und als PNG
 * neu kodiert. Die Pixel-Rechnungen sind rein und getestet.
 */

export const BILD_MAX_BREITE = 1200;
export const BILD_MAX_HOEHE = 2000;
/** Etwas unter dem Server-Limit (512 KB), damit Rundungen nicht zum 400 führen. */
const ZIEL_BYTES = 500 * 1024;
const WEISS_SCHWELLE = 235;

/** Pixel mit R, G, B > 235 → Alpha 0 (weißes Papier verschwindet, Tinte bleibt). */
export function weissZuTransparent(px: Uint8ClampedArray): void {
    for (let i = 0; i < px.length; i += 4) {
        if (px[i] > WEISS_SCHWELLE && px[i + 1] > WEISS_SCHWELLE && px[i + 2] > WEISS_SCHWELLE) px[i + 3] = 0;
    }
}

export function zielGroesse(w: number, h: number, maxW = BILD_MAX_BREITE, maxH = BILD_MAX_HOEHE): { w: number; h: number } {
    const f = Math.min(1, maxW / w, maxH / h);
    return { w: Math.max(1, Math.round(w * f)), h: Math.max(1, Math.round(h * f)) };
}

/** Rechteck um alle nicht durchsichtigen Pixel plus `rand`, begrenzt auf die Fläche. */
export function inhaltsRahmen(
    px: Uint8ClampedArray,
    w: number,
    h: number,
    rand: number,
): { x: number; y: number; w: number; h: number } | null {
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            if (px[(y * w + x) * 4 + 3] > 0) {
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                if (y > y1) y1 = y;
            }
        }
    }
    if (x1 < 0) return null;
    const x = Math.max(0, x0 - rand);
    const y = Math.max(0, y0 - rand);
    return { x, y, w: Math.min(w, x1 + rand + 1) - x, h: Math.min(h, y1 + rand + 1) - y };
}

export function blobAlsDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error("Das Bild ließ sich nicht lesen."));
        r.readAsDataURL(blob);
    });
}

export function canvasAlsPng(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("PNG konnte nicht erzeugt werden."))), "image/png"),
    );
}

/**
 * Hochgeladenes Foto/Scan → PNG-Data-URL: höchstens 1200 px breit, weißer Hintergrund
 * durchsichtig, unter 512 KB (sonst schrittweise kleiner).
 */
export async function bildDateiVorbereiten(datei: File): Promise<string> {
    if (!["image/png", "image/jpeg"].includes(datei.type)) {
        throw new Error("Bitte ein PNG- oder JPG-Bild wählen.");
    }
    const bitmap = await createImageBitmap(datei);
    try {
        let { w, h } = zielGroesse(bitmap.width, bitmap.height);
        for (let versuch = 0; versuch < 6; versuch++) {
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) throw new Error("Canvas wird von diesem Browser nicht unterstützt.");
            ctx.drawImage(bitmap, 0, 0, w, h);
            const daten = ctx.getImageData(0, 0, w, h);
            weissZuTransparent(daten.data);
            ctx.putImageData(daten, 0, 0);
            const png = await canvasAlsPng(canvas);
            if (png.size <= ZIEL_BYTES) return blobAlsDataUrl(png);
            w = Math.max(1, Math.round(w * 0.75));
            h = Math.max(1, Math.round(h * 0.75));
        }
        throw new Error("Das Bild ist zu groß — bitte ein kleineres wählen.");
    } finally {
        bitmap.close();
    }
}
